import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { toast } from 'react-hot-toast';
import { CREATE_TEAM, GET_TEAMS } from '../../graphql/teams';
import { 
  validateTeamForm, 
  getDefaultTeamFormData, 
  DEFAULT_TEAM_COLORS,
  TEAM_DEPARTMENT_OPTIONS
} from './teams-constants';
import { devLog, devError } from './constants';

export default function CreateTeamModal({ isOpen, onClose }) {
  const [formData, setFormData] = useState(getDefaultTeamFormData());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  const [createTeam] = useMutation(CREATE_TEAM, {
    refetchQueries: [{ query: GET_TEAMS }],
    onCompleted: () => {
      devLog('Team created successfully');
      toast.success(`Team "${formData.name}" created successfully!`);
      resetForm();
      onClose();
    },
    onError: (error) => {
      devError('Error creating team:', error);
      const errorMessage = error.message || 'Failed to create team';
      setError(errorMessage);
      toast.error(errorMessage);
    }
  });

  const resetForm = () => {
    setFormData(getDefaultTeamFormData());
    setError(null);
    setValidationErrors({});
    setIsLoading(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setValidationErrors({});

    // Validate form
    const validation = validateTeamForm(formData);
    if (!validation.isValid) {
      setValidationErrors(validation.errors);
      return;
    }

    setIsLoading(true);

    try {
      await createTeam({
        variables: {
          createTeamInput: {
            name: formData.name.trim(),
            description: formData.description?.trim() || null,
            color: formData.color || null,
            isContractor: formData.isContractor,
            department: formData.department
          }
        }
      });
    } catch (err) {
      devError('Submit error:', err);
      setError(err.message || 'Failed to create team');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
          <div className="px-6 py-4 border-b flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">Create New Team</h3>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-500"
              disabled={isLoading}
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6">
            {error && (
              <div className="mb-4 p-3 text-sm bg-red-50 border border-red-200 text-red-600 rounded">
                {error}
              </div>
            )}

            <div className="space-y-4">
              {/* Team Name */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  Team Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  id="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  maxLength={50}
                  className={`mt-1 block w-full rounded-md border ${
                    validationErrors.name ? 'border-red-500' : 'border-gray-300'
                  } p-2 shadow-sm focus:border-sky-500 focus:ring-sky-500`}
                  placeholder="Enter team name"
                  disabled={isLoading}
                />
                {validationErrors.name && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.name}</p>
                )}
              </div>

              <div>
                <label htmlFor="department" className="block text-sm font-medium text-gray-700">
                  Department <span className="text-red-500">*</span>
                </label>
                <select
                  name="department"
                  id="department"
                  value={formData.department}
                  onChange={handleInputChange}
                  required
                  className={`mt-1 block w-full rounded-md border ${
                    validationErrors.department ? 'border-red-500' : 'border-gray-300'
                  } p-2 shadow-sm focus:border-sky-500 focus:ring-sky-500`}
                  disabled={isLoading}
                >
                  <option value="">Select a department</option>
                  {TEAM_DEPARTMENT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
                {validationErrors.department && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.department}</p>
                )}
              </div>

              {/* Description */}
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                  Description
                </label>
                <textarea
                  name="description"
                  id="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  maxLength={255}
                  rows={3}
                  className={`mt-1 block w-full rounded-md border ${
                    validationErrors.description ? 'border-red-500' : 'border-gray-300'
                  } p-2 shadow-sm focus:border-sky-500 focus:ring-sky-500`}
                  placeholder="Enter team description (optional)"
                  disabled={isLoading}
                />
                {validationErrors.description && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.description}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  {formData.description.length}/255 characters
                </p>
              </div>

              {/* Color */}
              <div>
                <label htmlFor="color" className="block text-sm font-medium text-gray-700">
                  Team Color
                </label>
                <div className="mt-1 flex items-center gap-3">
                  <input
                    type="color"
                    name="color"
                    id="color"
                    value={formData.color}
                    onChange={handleInputChange}
                    className="h-10 w-16 rounded border border-gray-300 cursor-pointer disabled:cursor-not-allowed"
                    disabled={isLoading}
                  />
                  <div className="flex flex-wrap gap-2">
                    {DEFAULT_TEAM_COLORS.slice(0, 5).map((color) => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, color }))}
                        className={`w-6 h-6 rounded-full border-2 ${
                          formData.color === color ? 'border-gray-600' : 'border-gray-300'
                        } hover:border-gray-500`}
                        style={{ backgroundColor: color }}
                        disabled={isLoading}
                        title={`Select ${color}`}
                      />
                    ))}
                  </div>
                </div>
                {validationErrors.color && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.color}</p>
                )}
              </div>

              {/* Contractor Checkbox */}
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="isContractor"
                    checked={formData.isContractor}
                    onChange={handleInputChange}
                    disabled={isLoading}
                    className="h-4 w-4 text-sky-600 focus:ring-sky-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-700">
                    Contractor Team
                  </span>
                </label>
                <p className="mt-1 text-xs text-gray-500">
                  Mark this team as a contractor team for reporting purposes
                </p>
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-sky-600 hover:bg-sky-700 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isLoading}
              >
                {isLoading ? 'Creating...' : 'Create Team'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
} 